/**
 * Friendly, human-readable routes — no UUIDs anywhere in the path:
 *   /                       → the boards index (pick / search / create a board)
 *   /members                → the (global) members page
 *   /:slug                  → a board, default Kanban view      (slug = board.slug)
 *   /:slug/kanban|table|timeline → a board's specific view
 *   /:slug/:itemKey         → a task's full-screen page          (itemKey = e.g. SPB-3)
 */
export const ROUTES = {
  HOME: '/',
  MEMBERS: '/members',
  WHITEBOARDS: '/whiteboards',
  WIKI: '/wiki',
  SEARCH: '/search',
  MY_COMMENTS: '/me/comments',
  REPORTS: '/reports',
} as const;

/** The cross-board global search results page, carrying the raw query string in `?query=`.
 *  NB: deliberately NOT `?q` — that key is owned by the board-filter URL mirror (UrlSync). */
export const searchPath = (q: string): string => (q ? `/search?query=${encodeURIComponent(q)}` : '/search');

/** A full shareable search VIEW: the query plus the table's columns + sort (so a link / saved search
 *  reproduces the exact table). `cols`/`sort` are the raw param strings the /search page reads. */
export const searchViewPath = (q: string, cols?: string | null, sort?: string | null): string => {
  const p = new URLSearchParams();
  if (q) p.set('query', q);
  if (cols) p.set('cols', cols);
  if (sort) p.set('sort', sort);
  const s = p.toString();
  return s ? `/search?${s}` : '/search';
};

/** The whiteboards index (a top-level area, sibling of the boards index). */
export const whiteboardsPath = (): string => '/whiteboards';

/** A single whiteboard's full-screen infinite canvas. Keyed by id (the canvas is a standalone view). */
export const whiteboardPath = (id: string): string => `/whiteboards/${id}`;

/** The Wiki spaces index (top-level area, sibling of boards + whiteboards). */
export const wikiPath = (): string => '/wiki';
/** A space's page tree, by space slug. */
export const wikiSpacePath = (spaceSlug: string): string => `/wiki/${spaceSlug}`;
/** An open page inside a space (space slug + page slug). */
export const wikiPagePath = (spaceSlug: string, pageSlug: string): string => `/wiki/${spaceSlug}/${pageSlug}`;
/** Legacy UUID deep-link — resolves via a redirect shim to the friendly URL (see WikiArea). */
export const wikiLegacyPagePath = (pageId: string): string => `/wiki/p/${pageId}`;

export type BoardViewKey = 'kanban' | 'table' | 'timeline';

/** The three backlog views as URL segments under /:slug. Kanban is the default (empty segment). */
export const BOARD_VIEWS: { key: BoardViewKey; segment: string; label: string }[] = [
  { key: 'kanban', segment: '', label: 'Kanban' },
  { key: 'table', segment: 'table', label: 'Table' },
  { key: 'timeline', segment: 'timeline', label: 'Timeline' },
];

/** Path to a board view. Kanban (default) → /:slug; others → /:slug/<segment>. */
export const boardPath = (slug: string, view: BoardViewKey = 'kanban'): string => {
  const seg = BOARD_VIEWS.find((v) => v.key === view)?.segment ?? '';
  return seg ? `/${slug}/${seg}` : `/${slug}`;
};

/** Path to a task's full-screen page (itemKey is the human key, e.g. SPB-3). */
export const taskPath = (slug: string, itemKey: string): string => `/${slug}/${itemKey}`;

/** The view key for a location's last path segment (default kanban). */
export const viewKeyOf = (lastSegment: string | undefined): BoardViewKey => {
  const hit = BOARD_VIEWS.find((v) => v.segment === (lastSegment ?? ''));
  return hit ? hit.key : 'kanban';
};

/** True when running inside the Spark Boards desktop shell (Tauri), which injects this flag before the
 *  page's own scripts. Used so board/item links open as NEW desktop tabs (`target="_blank"`) instead of
 *  navigating the current tab in place — while a normal browser keeps single-tab navigation. */
export const isDesktopApp = (): boolean =>
  typeof window !== 'undefined' && !!(window as { __SPARK_DESKTOP__?: boolean }).__SPARK_DESKTOP__;

/** Link props that make an in-app navigation open a new desktop tab (only inside the desktop shell). In a
 *  browser this is a no-op, so React Router handles it as an ordinary client-side navigation. */
export const desktopNewTab = (): { target?: '_blank' } => (isDesktopApp() ? { target: '_blank' } : {});
